import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import BatchFormModal from '../components/BatchFormModal';

const PAGE_SIZE = 10;

const InventoryTab = () => {
  const [batches, setBatches] = useState([]);
  const [summary, setSummary] = useState({
    active_batches: 0,
    total_stock_value: 0,
    expiring_count: 0,
    low_stock_count: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch]);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getBatches(params),
        chemicalService.getBatchesSummary(),
      ]);

      setBatches(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { active_batches: 0, total_stock_value: 0, expiring_count: 0, low_stock_count: 0 });
    } catch (err) {
      console.error('Error fetching batches:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load batches'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const handleAddClick = () => {
    setEditingBatch(null);
    setShowFormModal(true);
  };

  const handleEditClick = (b) => {
    setEditingBatch(b);
    setShowFormModal(true);
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete batch "${b.batch_no}" (${b.product_name})?\n\nThis cannot be undone.`)) return;
    try {
      await chemicalService.deleteBatch(b.id);
      alert('✅ Batch deleted successfully!');
      if (batches.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchBatches();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete batch'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingBatch(null);
    fetchBatches();
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #2c3e50' }}>
          <div style={styles.summaryLabel}>Active Batches</div>
          <div style={{ ...styles.summaryValue, color: '#2c3e50' }}>{summary.active_batches}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #27ae60' }}>
          <div style={styles.summaryLabel}>Total Stock Value</div>
          <div style={{ ...styles.summaryValue, color: '#27ae60' }}>
            {formatLakh(summary.total_stock_value)}
          </div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f39c12' }}>
          <div style={styles.summaryLabel}>Expiring (30 days)</div>
          <div style={{ ...styles.summaryValue, color: '#f39c12' }}>{summary.expiring_count}</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>Low Stock Items</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>{summary.low_stock_count}</div>
        </div>
      </div>

      {/* ─── Filters + Search + Add ──────────────── */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {[
            { id: 'all', label: 'All' },
            { id: 'active', label: 'Active' },
            { id: 'low', label: 'Low' },
            { id: 'expiring', label: '⏱ Expiring' },
            { id: 'expired', label: 'Expired' },
            { id: 'empty', label: 'Empty' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              style={{
                ...styles.filterButton,
                background: statusFilter === f.id ? '#e74c3c' : 'white',
                color: statusFilter === f.id ? 'white' : '#2c3e50',
                borderColor: statusFilter === f.id ? '#e74c3c' : '#e0e0e0',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search batch no., product, location..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={handleAddClick} style={styles.addButton}>
            + Add batch
          </button>
        </div>
      </div>

      {/* ─── Batches Table ───────────────────────── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Batch No.</th>
              <th style={styles.th}>Product</th>
              <th style={styles.th}>Mfg Date</th>
              <th style={styles.th}>Expiry</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Qty Available</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Cost / Unit</th>
              <th style={styles.th}>Location</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading batches...
                </td>
              </tr>
            ) : batches.length === 0 ? (
              <tr>
                <td colSpan="9" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>🗃️</div>
                  {debouncedSearch || statusFilter !== 'all'
                    ? 'No batches match your filters'
                    : 'No batches yet — click "+ Add batch" to create one'}
                </td>
              </tr>
            ) : (
              batches.map((b) => {
                const qtyAvailable = parseFloat(b.qty_available) || 0;
                const qtyReceived = parseFloat(b.qty_received) || 0;
                const consumed = qtyReceived - qtyAvailable;
                const consumedPct = qtyReceived > 0 ? (consumed / qtyReceived) * 100 : 0;
                const daysUntilExpiry = b.expiry_date
                  ? Math.floor((new Date(b.expiry_date) - new Date()) / (1000 * 60 * 60 * 24))
                  : null;

                return (
                  <tr key={b.id} style={styles.row}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '700', color: '#2c3e50' }}>{b.batch_no}</div>
                      {b.source && b.source !== 'manual' && (
                        <div style={styles.sourceTag}>via {b.source}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>{b.product_name}</div>
                      <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>
                        {b.product_code} · {b.product_category || formatProductType(b.product_type)}
                      </div>
                    </td>
                    <td style={styles.td}>{formatDate(b.mfg_date)}</td>
                    <td style={styles.td}>
                      {b.expiry_date ? (
                        <div>
                          <div>{formatDate(b.expiry_date)}</div>
                          {daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry >= 0 && (
                            <div style={{ fontSize: '11px', color: '#f39c12', fontWeight: '700', marginTop: '2px' }}>
                              in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}
                            </div>
                          )}
                          {daysUntilExpiry !== null && daysUntilExpiry < 0 && (
                            <div style={{ fontSize: '11px', color: '#e74c3c', fontWeight: '700', marginTop: '2px' }}>
                              expired
                            </div>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <div style={{ fontWeight: '700', color: '#2c3e50' }}>
                        {formatQty(qtyAvailable)} {b.product_unit}
                      </div>
                      {qtyReceived > 0 && consumed > 0 && (
                        <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
                          of {formatQty(qtyReceived)} ({Math.round(consumedPct)}% used)
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {b.cost_per_unit != null
                        ? `Rs ${formatNumber(b.cost_per_unit)}`
                        : '—'}
                    </td>
                    <td style={styles.td}>
                      {b.location
                        ? <span style={styles.locationBadge}>📍 {b.location}</span>
                        : '—'}
                    </td>
                    <td style={styles.td}>
                      <span style={getStatusBadgeStyle(b.status)}>
                        {formatStatus(b.status)}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button onClick={() => handleEditClick(b)} style={styles.editButton} title="Edit">
                          ✏️
                        </button>
                        <button onClick={() => handleDelete(b)} style={styles.deleteButton} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ──────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} batches
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Add/Edit Modal ──────────────────────── */}
      {showFormModal && (
        <BatchFormModal
          batch={editingBatch}
          onClose={() => { setShowFormModal(false); setEditingBatch(null); }}
          onSaved={handleSaved}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return Math.round(num).toLocaleString('en-PK');
};

const formatQty = (n) => {
  const num = parseFloat(n) || 0;
  return num % 1 === 0 ? num.toLocaleString('en-PK') : num.toFixed(2);
};

const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 100000) {
    return `Rs ${(num / 100000).toFixed(1)}L`;
  }
  return `Rs ${formatNumber(num)}`;
};

const formatStatus = (status) => {
  const map = {
    active:   '✓ Active',
    low:      'Low Stock',
    expiring: '⏱ Expiring',
    expired:  '✕ Expired',
    empty:    'Empty',
  };
  return map[status] || status;
};

const formatProductType = (type) => {
  const map = { raw: 'Raw', finished: 'Finished', trading: 'Trading' };
  return map[type] || type;
};

const getStatusBadgeStyle = (status) => {
  const palette = {
    active:   { bg: '#d4edda', fg: '#155724' },
    low:      { bg: '#f8d7da', fg: '#721c24' },
    expiring: { bg: '#fff3cd', fg: '#856404' },
    expired:  { bg: '#f8d7da', fg: '#721c24' },
    empty:    { bg: '#e2e3e5', fg: '#383d41' },
  };
  const c = palette[status] || { bg: '#e2e3e5', fg: '#383d41' };
  return {
    background: c.bg, color: c.fg,
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
    display: 'inline-block',
    whiteSpace: 'nowrap',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: {
    fontSize: '13px', color: '#7f8c8d',
    marginBottom: '6px', fontWeight: '600',
  },
  summaryValue: {
    fontSize: '28px', fontWeight: '700',
  },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterButton: {
    padding: '8px 16px',
    border: '2px solid', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  toolbarRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '280px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 20px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  sourceTag: {
    display: 'inline-block',
    fontSize: '10px', color: '#7f8c8d',
    background: '#f0f0f0', padding: '2px 8px',
    borderRadius: '8px', marginTop: '3px',
    fontWeight: '600', textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  locationBadge: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '4px 10px', borderRadius: '10px',
    fontSize: '12px', fontWeight: '600',
    display: 'inline-block', whiteSpace: 'nowrap',
  },

  editButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: {
    opacity: 0.4, cursor: 'not-allowed',
  },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default InventoryTab;